import React, {
  useState, useEffect, useRef, ReactNode, KeyboardEvent,
} from 'react';
// import styled from 'styled-components';
import {
  Remirror, ReactFrameworkOutput, ThemeProvider, useRemirror,
} from '@remirror/react';
import {
  Extension, RemirrorJSON, prosemirrorNodeToHtml,
} from '@remirror/core';
import {
  HistoryExtension, TextExtension, PositionerExtension,
} from 'remirror/extensions';
import { CountExtension, CountStrategy } from '@remirror/extension-count';
import { AllStyledComponent } from '@remirror/styles/styled-components';
import 'remirror/styles/all.css';
import sanitizeHtml from 'sanitize-html';

import { RefocusExtension } from './extensions/custom/refocus';

import TextEditor from './view';

// const Indicator = styled(SocialCharacterCountWrapper)`
//   z-index: 99;
//   &,.remirror-character-count-circle {
//     stroke: ${({ theme }) => theme.primaryColour} !important;
//   }
// `;

export const DEFAULT_CONTENT = '<p></p>';

export const sanitiseValue = (value) => sanitizeHtml(value, {
  allowedTags: sanitizeHtml.defaults.allowedTags.concat(['a', 'img', 'iframe', 'video', 'span', 'h1', 'h2']),
  allowedAttributes: {
    a: ['href', 'name', 'rel', 'target', 'auto', 'class', 'data-mention-atom-id', 'data-mention-atom-name', 'style'],
    img: ['src', 'width', 'height'],
    div: ['style'], // alignment
    span: ['src', 'class', 'data-ext', 'data-node', 'style'], // file
    i: ['class'],
    iframe: ['src', 'allowfullscreen', 'width', 'height'],
    video: ['src', 'allowfullscreen', 'width', 'height'],
  },
  allowedIframeHostnames: ['youtube.com', 'player.vimeo.com'],
});

type IEditorWrapper = {
  value?: string,
  onChange?: (value: string) => void,
  onChangeJson?: (value: RemirrorJSON) => void,
  onBlur?: (value: string) => void,
  onFocus?: (params: any, event: any) => void,
  emptyReturnValue?: string,
  wordLimit?: number,
  extensions?: Extension[],
  children?: ReactNode,
  readOnly?: boolean,
  placeholder?: string,
  noShadow?: boolean,
  style?: object,
  editorRef?: any,
  handleKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void,
}

const EditorWrapper = ({
  value, onChange, onChangeJson, onBlur, onFocus, emptyReturnValue = DEFAULT_CONTENT, wordLimit, extensions, children, readOnly, placeholder, noShadow, style, editorRef: propRef, handleKeyDown,
}: IEditorWrapper) => {
  const _localRef = useRef<ReactFrameworkOutput<Extension>>();
  const editorRef: typeof _localRef = propRef ?? _localRef;
  const [allExtensions] = useState<() => Extension[]>(() => ([
    ...extensions,
    new RefocusExtension({}),
    new PositionerExtension({}),
    new HistoryExtension({}),
    new TextExtension(),
    wordLimit ? new CountExtension({ maximum: wordLimit, maximumStrategy: CountStrategy.WORDS }) : null,
  ] as Extension[]).filter((extension) => !!extension));

  const {
    manager,
    state: remirrorState,
  } = useRemirror({
    extensions: allExtensions,
    content: value || DEFAULT_CONTENT,
    stringHandler: 'html',
    selection: 'end',
  });

  useEffect(() => {
    if (!editorRef.current) {
      return;
    }
    // const currentHTML = sanitiseValue(prosemirrorNodeToHtml({
    //   node: editorRef.current.getState().doc,
    //   schema: editorRef.current.schema,
    // }));

    const currentHTML = sanitiseValue(prosemirrorNodeToHtml(editorRef.current.getState().doc));

    const newHTML = sanitiseValue(value || DEFAULT_CONTENT);
    if (currentHTML === newHTML) {
      return;
    }

    manager.view.updateState(manager.createState({ content: newHTML }));
  }, [value, manager, editorRef]);

  useEffect(() => {
    // @ts-ignore
    if (!editorRef?.current) {
      return;
    }
    // @ts-ignore
    if (editorRef.current.view.manager) {
      return;
    }
    // @ts-ignore
    editorRef.current.view.manager = manager;
  }, [editorRef, manager]);

  return (
    <AllStyledComponent>
      <ThemeProvider>
        <Remirror
          manager={manager}
          initialContent={remirrorState}
          editable={!readOnly}
          placeholder={placeholder}
          onFocus={onFocus}
          onBlur={(change) => {
            if (readOnly) return;
            if (onBlur) {
              const html = change.helpers.getHTML();
              if (html === DEFAULT_CONTENT) {
                onBlur(emptyReturnValue);
              } else {
                onBlur(sanitiseValue(html));
              }
            }
          }}
          // @ts-ignore
          onKeyDown={handleKeyDown}
          onChange={(change) => {
            const { tr } = change;
            // Update internal editor state to using the new value

            const metaHistory = tr?.getMeta('history$');

            if (change.internalUpdate) return; // don't report internal updates
            if (metaHistory) return; // don't report history updates (which are disabled but still register as a transaction)
            if (readOnly) return; // read only never reports

            const html = change.helpers.getHTML();
            const sanitised = sanitiseValue(html);
            if (sanitiseValue(value) === sanitised) return;
            if (!value && html === DEFAULT_CONTENT) return;

            if (onChangeJson) {
              onChangeJson(change.helpers.getRemirrorJSON());
            }
            if (onChange) {
              // Is get html doing some sanitisation?!
              if (html === DEFAULT_CONTENT) {
                onChange(emptyReturnValue);
              } else {
                onChange(sanitised);
              }
            }
          }}
        >
          <div style={{
            flex: 1,
            alignSelf: 'stretch',
            position: 'relative',
            wordBreak: 'break-all',
          }}
          >
            {!readOnly ? (
              <>
                {children}
              </>
            ) : null}
            <TextEditor
              ref={editorRef}
              readOnly={readOnly}
              noShadow={noShadow}
              style={style}
            />
          </div>
        </Remirror>
      </ThemeProvider>
    </AllStyledComponent>

  );
};

export default React.memo(EditorWrapper);
